import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '../utils.ts';

interface SkeletonProps extends ComponentPropsWithoutRef<'div'> {
  /** Render as a paragraph block of n lines. */
  lines?: number;
}

const shimmer =
  'relative overflow-hidden bg-light-silver/80 before:absolute before:inset-0 before:-translate-x-full ' +
  'before:animate-[shimmer_1.6s_ease-in-out_infinite] before:bg-gradient-to-r before:from-transparent ' +
  'before:via-white/70 dark:before:via-white/10 before:to-transparent motion-reduce:before:animate-none';

export function Skeleton({ lines, className, ...rest }: SkeletonProps) {
  if (lines && lines > 0) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: <output> is for form output; this is a loading placeholder where role="status" is the widely-accepted ARIA pattern.
      <div className={cn('space-y-2', className)} role="status" aria-label="Loading">
        {Array.from({ length: lines }).map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: list is generated from a length count, never reorders.
            key={i}
            className={cn('h-3 rounded-sm', shimmer, i === lines - 1 ? 'w-3/5' : 'w-full')}
          />
        ))}
        <span className="sr-only">Loading</span>
      </div>
    );
  }
  return (
    <div
      // biome-ignore lint/a11y/useSemanticElements: same as above; a non-form loading placeholder.
      role="status"
      aria-label="Loading"
      className={cn('h-4 rounded-sm', shimmer, className)}
      {...rest}
    >
      <span className="sr-only">Loading</span>
    </div>
  );
}
